/**
 * Pagination Utilities Module
 * Sprint 29: Reusable pagination logic for lists and tables
 *
 * Following OOA/OOD principles:
 * - Single Responsibility: Only pagination logic
 * - DRY: Reusable across devices, activity logs, etc.
 */

/**
 * Pagination state interface
 */
export interface PaginationState {
    offset: number;
    limit: number;
    total: number;
}

/**
 * Pagination UI element IDs
 */
export interface PaginationElements {
    infoId: string;           // Element showing "Showing X-Y of Z"
    prevButtonId: string;     // Previous button ID
    nextButtonId: string;     // Next button ID
}

/**
 * Create initial pagination state
 */
export function createPaginationState(limit: number = 10): PaginationState {
    return {
        offset: 0,
        limit,
        total: 0
    };
}

/**
 * Update pagination UI elements
 * @param state Current pagination state
 * @param elements Element IDs for pagination UI
 */
export function updatePaginationUI(state: PaginationState, elements: PaginationElements): void {
    const infoEl = document.getElementById(elements.infoId);
    const prevBtn = document.getElementById(elements.prevButtonId) as HTMLButtonElement;
    const nextBtn = document.getElementById(elements.nextButtonId) as HTMLButtonElement;

    // Calculate display values
    const startNum = state.total > 0 ? state.offset + 1 : 0;
    const endNum = Math.min(state.offset + state.limit, state.total);
    const hasMultiplePages = state.total > state.limit;

    // Update info text
    if (infoEl) {
        infoEl.textContent = `Showing ${startNum}-${endNum} of ${state.total}`;
    }

    // Update Previous button
    if (prevBtn) {
        prevBtn.disabled = state.offset === 0;
        if (!hasMultiplePages) {
            prevBtn.classList.add('hidden');
        } else {
            prevBtn.classList.remove('hidden');
        }
    }

    // Update Next button
    if (nextBtn) {
        nextBtn.disabled = state.offset + state.limit >= state.total;
        if (!hasMultiplePages) {
            nextBtn.classList.add('hidden');
        } else {
            nextBtn.classList.remove('hidden');
        }
    }
}

/**
 * Go to previous page
 * @param state Current pagination state
 * @returns Updated state
 */
export function goToPrevPage(state: PaginationState): PaginationState {
    return {
        ...state,
        offset: Math.max(0, state.offset - state.limit)
    };
}

/**
 * Go to next page
 * @param state Current pagination state
 * @returns Updated state
 */
export function goToNextPage(state: PaginationState): PaginationState {
    if (state.offset + state.limit < state.total) {
        return {
            ...state,
            offset: state.offset + state.limit
        };
    }
    return state;
}

/**
 * Reset pagination to first page
 * @param state Current pagination state
 * @param newTotal Optional new total count
 * @returns Updated state
 */
export function resetPagination(state: PaginationState, newTotal?: number): PaginationState {
    return {
        ...state,
        offset: 0,
        total: newTotal !== undefined ? newTotal : state.total
    };
}

/**
 * Get slice of items for current page
 * @param items All items
 * @param state Pagination state
 * @returns Items for current page
 */
export function getPageItems<T>(items: T[], state: PaginationState): T[] {
    return items.slice(state.offset, state.offset + state.limit);
}

/**
 * Set up pagination event listeners
 * @param elements Pagination element IDs
 * @param onPageChange Callback when page changes
 * @param getState Function to get current state
 * @param setState Function to update state
 */
export function setupPaginationListeners(
    elements: PaginationElements,
    onPageChange: () => void,
    getState: () => PaginationState,
    setState: (state: PaginationState) => void
): void {
    const prevBtn = document.getElementById(elements.prevButtonId);
    const nextBtn = document.getElementById(elements.nextButtonId);

    if (prevBtn) {
        prevBtn.addEventListener('click', () => {
            setState(goToPrevPage(getState()));
            onPageChange();
        });
    }

    if (nextBtn) {
        nextBtn.addEventListener('click', () => {
            setState(goToNextPage(getState()));
            onPageChange();
        });
    }
}
